import { TypeListItem, TypeListItemCollectionFields } from "@contentful/types";
import { Icon, isIconName } from "@yardzen/ui/components/icon";
import { ListItem as UIListItem } from "@yardzen/ui/components/list-item";
import { ToggleCard } from "@yardzen/ui/components/toggle-card";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import { EntryFieldTypes } from "contentful";

type ExtractSymbol<T> = T extends EntryFieldTypes.Symbol<infer U> ? U : never;

export function ListItem({
  listItem,
  variant,
  hideSubHeading,
  className,
  headingClassName,
  subHeadingClassName,
  iconClassName,
  iconWrapperClassName,
  iconSize,
}: {
  listItem: TypeListItem<"WITHOUT_UNRESOLVABLE_LINKS">;
  variant?: ExtractSymbol<TypeListItemCollectionFields["variant"]>;
  hideSubHeading?: boolean;
  className?: string;
  headingClassName?: string;
  subHeadingClassName?: string;
  iconClassName?: string;
  iconWrapperClassName?: string;
  iconSize?: "sm" | "md" | "lg";
}) {
  const icon = listItem?.fields.icon;
  const heading = listItem?.fields.heading ?? "";
  // Shop-card treatment (soft green gradient, à la the design-delivery
  // shopping panel) for partner/member-pricing items. Triggered by brand
  // logos on the entry; the heading match is a stopgap until the content
  // model grows a proper flag (tracked in the CMS backlog ticket).
  const isShopCard =
    Boolean(listItem?.fields.logoGrid?.length) ||
    /member pricing/i.test(heading);
  const subheading = listItem?.fields.subheading ?? "";
  const description = listItem?.fields.description ?? null;
  const bgColorResolved = listItem?.fields.backgroundColor ?? "none";
  const iconBgColorResolved = listItem?.fields.iconBackgroundColor ?? "none";
  const logoGrid = listItem?.fields.logoGrid;

  const iconVariants = cva("", {
    variants: {
      bgColor: {
        white: "bg-white p-4",
        none: "",
      },
      iconBgColor: {
        green: "bg-brand-moss-greens-10",
        "light-grey": "bg-system-grayscale-10",
        none: "",
      },
    },
  });

  return variant === "icon-above" ? (
    icon && isIconName(icon) && (
      <ToggleCard
        className="h-20 border-none !bg-white p-4 no-underline shadow-none"
        disabled={true}
        icons={[<Icon name={icon} className="text-brand-primary-regular" />]}
        title={heading}
      />
    )
  ) : (
    <UIListItem
      iconName={icon}
      heading={heading}
      subheading={!hideSubHeading ? subheading : undefined}
      className={cn(
        className,
        iconVariants({ bgColor: bgColorResolved }),
        isShopCard &&
          "from-brand-moss-greens-10/70 rounded-xl bg-gradient-to-r to-white/40 p-4",
      )}
      iconSize={iconSize}
      iconClassName={iconClassName}
      iconWrapperClassName={cn(
        iconWrapperClassName,
        iconVariants({ iconBgColor: iconBgColorResolved }),
      )}
      headingClassName={headingClassName}
      subHeadingClassName={subHeadingClassName}
      description={description}
      logoGrid={logoGrid}
    />
  );
}
